import { v4 as uuidv4 } from 'uuid';
import { marked } from 'marked';

export const generateUUID = (): string => {
  return uuidv4();
};

export const encodeURIComponentSafe = (str: string | null | undefined): string => {
  try {
    return encodeURIComponent(str || '');
  } catch (e) {
    console.error("Error encoding URI component:", str, e);
    return '';
  }
};

export const parseMarkdown = async (text: string): Promise<string> => {
  try {
    return await marked.parse(text);
  } catch (e) {
    console.error('Error parsing markdown:', e);
    // Fallback to basic markdown conversion
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br>');
  }
};

export const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString();
  } catch (e) {
    return dateString;
  }
};

export const getWineColorClass = (color: string): string => {
  const wineColor = (color || '').toLowerCase();
  if (wineColor === 'red') return 'red';
  if (wineColor === 'white') return 'white';
  if (wineColor === 'rosé' || wineColor === 'rose') return 'rose';
  return 'unknown';
};

export const getWarehouseFlag = (warehouseName: string): { filename: string; text: string } => {
  const warehouse = (warehouseName || '').toLowerCase();
  
  if (warehouse.includes('hong kong')) {
    return { filename: 'hk.png', text: 'HK' };
  } else if (warehouse.includes('united kingdom')) {
    return { filename: 'uk.png', text: 'UK' };
  } else if (warehouse.includes('bordeaux') || warehouse.includes('burgundy')) {
    return { filename: 'fr.png', text: 'FR' };
  } else if (warehouse.includes('singapore')) {
    return { filename: 'sg.png', text: 'SG' };
  } else {
    return { filename: 'default.png', text: '?' };
  }
};

export const buildWineUrls = (offer: any, user?: any): { moreInfoUrl: string; buyNowUrl: string } => {
  const baseUrl = "https://www.wwxplorer.com";
  
  // Extract and encode fields for new URL pattern
  const color = encodeURIComponentSafe(offer.color || 'Red');
  const wineType = encodeURIComponentSafe(offer.type || 'Still');
  const vintage = encodeURIComponentSafe(offer.vintage || 'NV');
  const packingSize = encodeURIComponentSafe(`${offer.quantity_per_case || '1'} x ${offer.volume || '75cl'}`);
  const country = 'all'; // Country not available in database, using 'all' as default
  const variety = '-'; // Variety not available in database, using '-' as placeholder
  const oid = encodeURIComponentSafe(offer.offer_oid || offer.offer_id);
  const displayName = encodeURIComponentSafe(offer.display_name || offer.processed_wine_name || '').toLowerCase().replace(/\s+/g, ' ');
  const producer = encodeURIComponentSafe(offer.processed_producer);

  const moreInfoUrl = `${baseUrl}/product${producer ? `/producer/${producer}` : ''}/product/${displayName}/color/${color}/type/${wineType}/vintage/${vintage}/packing/${packingSize}`;

  // Keep the buy now URL as is for now, or update if needed
  const wineHash = encodeURIComponentSafe(offer.wine_hash);
  const buyNowUrl = user 
    ? `${baseUrl}/place-order/wine/${wineHash}/${color}/${wineType}/-/${vintage}/${packingSize}?offerId=${offer.offer_id}`
    : `${baseUrl}/signup`;

  return { moreInfoUrl, buyNowUrl };
};

export const debounce = <T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void => {
  let timeout: number;
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait) as unknown as number;
  };
};

export const isMobile = (): boolean => {
  return typeof window !== 'undefined' && window.innerWidth <= 768;
};

export const storage = {
  get: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.error('Error reading from localStorage:', e);
      return null;
    }
  },
  
  set: (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.error('Error writing to localStorage:', e);
    }
  },

  remove: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error('Error removing from localStorage:', e);
    }
  }
};
